import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { usersApi } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useCurrentUser } from '@/hooks/useCurrentUser';

interface Client {
  id: number;
  name: string;
}

const ClientSelection = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const { setClient, user, selectedClient: storedClient } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Utiliser le hook pour récupérer l'utilisateur (avec cache React Query)
  // Cela évite de refaire l'appel API déjà fait dans login()
  const { data: currentUser, isLoading: isLoadingUser } = useCurrentUser();

  // Récupérer la liste des clients de l'utilisateur connecté
  const { data: clients, isLoading: isLoadingClients, error } = useQuery({
    queryKey: ['user-clients', currentUser?.id],
    queryFn: async () => {
      if (!currentUser?.id) {
        throw new Error('Utilisateur non connecté');
      }

      // Plus besoin de refaire l'appel by-email, on utilise directement currentUser du cache
      const userClients = await usersApi.getUserClientsUsersUserIdClientsGet(currentUser.id);
      
      return Array.isArray(userClients) ? userClients : [];
    },
    enabled: !!currentUser?.id, // Ne s'exécute que si currentUser est chargé
  });

  const isLoading = isLoadingUser || isLoadingClients;

  const clientsList: Client[] = clients || [];
  const useDropdown = clientsList.length > 4;

  const filteredClients = clientsList.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectChange = (value: string) => {
    const clientId = parseInt(value, 10);
    const client = clientsList.find(c => c.id === clientId);
    if (client) {
      setSelectedClient(client);
    }
  };

  useEffect(() => {
    if (error) {
      const errorMessage = error instanceof Error 
        ? error.message 
        : "Impossible de charger la liste des clients";
      
      toast({
        variant: "destructive",
        title: "Erreur",
        description: errorMessage,
      });
    }
  }, [error, toast]);

  // Réhydratation et validation du client stocké au chargement
  useEffect(() => {
    if (isLoading) {
      return;
    }

    // Si aucun client n'est disponible, nettoyer le client sélectionné
    if (clientsList.length === 0 && !error) {
      setClient('');
      return;
    }

    // Réhydrater le client depuis localStorage si pas déjà dans le contexte
    if (!storedClient && typeof window !== 'undefined' && clientsList.length > 0) {
      try {
        const stored = window.localStorage.getItem('enrichment_app_selected_client');
        if (stored) {
          // Vérifier si le client stocké existe dans la liste
          const clientExists = clientsList.some(
            (client) => client.name === stored
          );
          
          if (clientExists) {
            // Le client est valide, le réhydrater
            setClient(stored);
          } else {
            // Le client stocké n'existe plus, le nettoyer
            window.localStorage.removeItem('enrichment_app_selected_client');
          }
        }
      } catch (error) {
        console.error('Erreur lors de la réhydratation du client:', error);
      }
    }

    // Si un client est déjà dans le contexte, vérifier s'il existe toujours
    if (storedClient && clientsList.length > 0) {
      const clientExists = clientsList.some(
        (client) => client.name === storedClient
      );
      
      if (!clientExists) {
        // Le client stocké n'existe plus, le nettoyer
        setClient('');
        toast({
          variant: 'destructive',
          title: 'Client non disponible',
          description: "Le client sélectionné précédemment n'est plus accessible. Veuillez en choisir un autre.",
        });
      }
    }
  }, [isLoading, clientsList, storedClient, setClient, toast, error]);

  const handleValidate = () => {
    if (selectedClient) {
      // Stocker le nom du client dans le contexte (pour l'affichage dans la barre de navigation)
      setClient(selectedClient.name);
      navigate('/configuration');
    }
  };

  return (
    <div className="min-h-screen bg-muted/30 flex items-center justify-center p-4">
      <div className="relative w-full max-w-2xl">
        {/* Logo positionné de manière moderne (moitié sur gris, moitié sur noir) */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
          <img 
            src="/logo_W.png" 
            alt="Weborama Logo" 
            className="w-[64px] h-[64px] object-contain"
          />
        </div>
        <Card className="w-full shadow-card">
          <CardHeader className="text-center pt-8">
            <CardTitle className="text-2xl font-bold text-white">
              Sélection du client
            </CardTitle>
          <CardDescription>
            Choisissez un client pour continuer
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Rechercher un client..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {isLoading ? (
            <p className="text-center text-muted-foreground py-8">
              Chargement des clients...
            </p>
          ) : (
            <>
              {useDropdown ? (
                // Mode liste déroulante pour plus de 4 clients
                <div className="space-y-2">
                  <Select
                    value={selectedClient?.id.toString()}
                    onValueChange={handleSelectChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Sélectionner un client..." />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredClients.length === 0 ? (
                        <div className="px-2 py-1.5 text-sm text-muted-foreground text-center">
                          {clientsList.length === 0 
                            ? "Aucun client disponible"
                            : "Aucun client trouvé avec ce critère"}
                        </div>
                      ) : (
                        filteredClients.map((client) => (
                          <SelectItem key={client.id} value={client.id.toString()}>
                            {client.id} - {client.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                // Mode boutons pour 4 clients ou moins
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredClients.map((client) => (
                    <button
                      key={client.id}
                      onClick={() => setSelectedClient(client)}
                      className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                        selectedClient?.id === client.id
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <span className="font-medium">{client.id} - {client.name}</span>
                    </button>
                  ))}

                  {filteredClients.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">
                      {clientsList.length === 0 
                        ? "Aucun client disponible pour votre compte"
                        : "Aucun client trouvé avec ce critère de recherche"}
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          <Button
            onClick={handleValidate}
            disabled={!selectedClient}
            className="w-full bg-primary hover:bg-primary-hover"
          >
            Valider
          </Button>
        </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClientSelection;
